import React, { useEffect, useState } from "react";
import {
  MdOutlineAccountBalanceWallet,
  MdPendingActions,
  MdAttachMoney,
  MdOutlinePayments
} from "react-icons/md";
import { FaMoneyBillWave } from "react-icons/fa";

import { getSellerBalanceApi } from "../../ApiCalls/Revenue";

const SellerRevenue = () => {
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchSellerBalance = async () => {
      try {
        const res = await getSellerBalanceApi();
        if (res?.status === 1) {
          setBalance(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch seller balance", error);
      } finally {
        setLoading(false);
      }
    };

    fetchSellerBalance();
  }, []);

  const StatCard = ({ title, value, icon: Icon, colorClass, subText }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 flex items-center justify-between">
      <div>
        <p className="text-xs text-gray-500 font-semibold uppercase">{title}</p>
        <p className="text-xl font-bold text-gray-800 mt-1">{value}</p>
        {subText && <p className="text-xs text-gray-400 mt-1">{subText}</p>}
      </div>
      <div className={`p-3 rounded-full ${colorClass}`}>
        <Icon size={24} />
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-gray-500 animate-pulse">Loading revenue data...</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8 min-h-screen bg-gray-50">
      <h1 className="text-2xl font-bold text-gray-800">Revenue Dashboard</h1>

      {/* Main Balance Card */}
      <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500 flex flex-col md:flex-row md:items-center justify-between">
        <div>
          <h2 className="text-gray-500 text-lg font-medium">Available Balance</h2>
          <div className="flex items-baseline mt-2">
            <span className="text-4xl font-bold text-gray-900">
              ₹{balance?.available_balance?.toLocaleString() || "0.00"}
            </span>
            <span className="ml-3 text-sm text-green-600 bg-green-100 px-3 py-1 rounded-full font-medium">
              Ready for Payout
            </span>
          </div>
        </div>

        <div className="mt-4 md:mt-0 text-right">
          <div className="text-sm text-gray-500">Last Updated</div>
          <div className="font-semibold text-gray-700">
            {balance?.last_updated
              ? new Date(balance.last_updated).toLocaleString()
              : "Never"}
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard
          title="Pending Balance"
          value={`₹${balance?.pending_balance?.toLocaleString() || "0.00"}`}
          icon={MdPendingActions}
          colorClass="bg-orange-100 text-orange-600"
          subText="Processing / On Hold"
        />

        <StatCard
          title="Total Earned"
          value={`₹${balance?.total_earned?.toLocaleString() || "0.00"}`}
          icon={MdAttachMoney}
          colorClass="bg-green-100 text-green-600"
          subText="Lifetime Earnings"
        />

        <StatCard
          title="COD Balance"
          value={`₹${balance?.cod_balance?.toLocaleString() || "0.00"}`}
          icon={FaMoneyBillWave}
          colorClass="bg-blue-100 text-blue-600"
          subText="Cash on Delivery"
        />

        <StatCard
          title="COD Fee Due"
          value={`₹${balance?.cod_service_fee_due?.toLocaleString() || "0.00"}`}
          icon={MdOutlinePayments}
          colorClass="bg-red-100 text-red-600"
          subText="Service Fee to Pay"
        />

        <StatCard
          title="Total Service Fee"
          value={`₹${balance?.service_fee_total?.toLocaleString() || "0.00"}`}
          icon={MdOutlineAccountBalanceWallet}
          colorClass="bg-purple-100 text-purple-600"
          subText="Platform Fees Paid"
        />

        <StatCard
          title="Pending Service Fee"
          value={`₹${balance?.service_fee_pending?.toLocaleString() || "0.00"}`}
          icon={MdPendingActions}
          colorClass="bg-yellow-100 text-yellow-600"
          subText="Fees yet to be deducted"
        />
      </div>
    </div>
  );
};

export default SellerRevenue;